import { ChartBinSize } from '@frontend/chart/types';
import { DateStep } from '@frontend/common';

import {
    getLast2WeeksHourDayChartRange,
    getLast6MonthsChartRange,
    getLast30DaysChartRange,
    getLast60DaysChartRange
} from '../../../statistics/pudo/last-30-days-chart-range';
import { InsightDefaultRangeKey } from './insight-filter-config';
import { BIN_SIZE_MAX_RANGE_MS } from './insight-filter-guards';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Preset durations used to decide which DateStep options fit a bin_size max window. */
const DATE_STEP_DURATION_MS: Partial<Record<DateStep, number>> = {
    [DateStep.DAY]: 1 * DAY_MS,
    [DateStep.WEEK]: 7 * DAY_MS,
    [DateStep.MONTH]: 30 * DAY_MS,
    [DateStep.YEAR]: 365 * DAY_MS
};

const PRESET_DATE_STEPS: DateStep[] = [DateStep.DAY, DateStep.WEEK, DateStep.MONTH, DateStep.YEAR, DateStep.FULL_PERIOD];

export const getDefaultInsightRange = (key: InsightDefaultRangeKey): { after: string; before: string } => {
    switch (key) {
        case '60d':
            return getLast60DaysChartRange();
        case '6mo': {
            const { after, before } = getLast6MonthsChartRange();
            return { after, before };
        }
        case '2w': {
            const { after, before } = getLast2WeeksHourDayChartRange();
            return { after, before };
        }
        case 'none':
            return { after: '', before: '' };
        case '30d':
        default:
            return getLast30DaysChartRange();
    }
};

export const rangeFromDateStep = (step: DateStep, binSize: ChartBinSize): { after: string; before: string } => {
    const before = new Date();
    const after = new Date(before);

    switch (step) {
        case DateStep.DAY:
            after.setUTCDate(before.getUTCDate() - 1);
            break;
        case DateStep.WEEK:
            after.setUTCDate(before.getUTCDate() - 7);
            break;
        case DateStep.MONTH:
            after.setUTCDate(before.getUTCDate() - 30);
            break;
        case DateStep.YEAR:
            after.setUTCDate(before.getUTCDate() - 365);
            break;
        case DateStep.FULL_PERIOD:
            after.setTime(before.getTime() - BIN_SIZE_MAX_RANGE_MS[binSize]);
            break;
        default:
            after.setUTCDate(before.getUTCDate() - 30);
            break;
    }

    return { after: after.toISOString(), before: before.toISOString() };
};

/** Whether a preset fits the max query window for the given bin_size. Steps without a fixed duration (Full period, Custom) are always allowed. */
export const isDateStepAllowedForBinSize = (step: DateStep, binSize: ChartBinSize): boolean => {
    const durationMs = DATE_STEP_DURATION_MS[step];
    if (durationMs == null) {
        return true;
    }
    return durationMs <= BIN_SIZE_MAX_RANGE_MS[binSize];
};

export const allowedDateStepsForBinSize = (binSize: ChartBinSize): DateStep[] => PRESET_DATE_STEPS.filter((step) => isDateStepAllowedForBinSize(step, binSize));

export type BinSizeMaxWindow = { count: number; unit: 'days' | 'years' };

/** Human-facing max window for Full period labeling (days, or whole years when exact). */
export const getBinSizeMaxWindow = (binSize: ChartBinSize): BinSizeMaxWindow => {
    const days = Math.round(BIN_SIZE_MAX_RANGE_MS[binSize] / DAY_MS);
    if (days >= 365 && days % 365 === 0) {
        return { count: days / 365, unit: 'years' };
    }
    return { count: days, unit: 'days' };
};

export const isValidIsoDate = (value: string | null | undefined): value is string => {
    if (!value) return false;
    return !Number.isNaN(Date.parse(value));
};

export const parseChartBinSize = (value: string | null | undefined): ChartBinSize | undefined => {
    if (!value) return undefined;
    return (Object.values(ChartBinSize) as string[]).includes(value) ? (value as ChartBinSize) : undefined;
};
